import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { publicApi } from "../hooks/useApi";
import { useAuthContext } from "../context/AuthContext";

export default function SignInPage() {
  const navigate = useNavigate();
  const { refresh } = useAuthContext();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await publicApi.post("/auth/login", { email, password });
      const { role } = await refresh();
      toast.success("Welcome back.");
      navigate(role === "admin" ? "/operator" : "/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "email_not_verified") {
        setNeedsVerify(true);
        setError(detail.message);
      } else {
        setError(detail?.message || "Incorrect email or password.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center px-4 py-12" data-testid="sign-in-page">
      <div className="text-center mb-8">
        <p className="font-display text-xl tracking-[0.2em] uppercase text-white">Endure Beyond</p>
        <p className="text-white/50 text-sm mt-2">Sign in to continue</p>
      </div>
      <form onSubmit={submit} className="w-full max-w-sm space-y-4">
        {error && (
          <div data-testid="sign-in-error" className="text-sm text-red-400">
            {error}
            {needsVerify && (
              <Link to={`/sign-up?email=${encodeURIComponent(email)}&step=verify`} className="underline ml-1">
                Verify now
              </Link>
            )}
          </div>
        )}
        <input
          type="email"
          required
          placeholder="Email"
          aria-label="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="sign-in-email-input"
          className="w-full bg-surface border border-border px-4 py-3 text-sm"
        />
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            required
            placeholder="Password"
            aria-label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="sign-in-password-input"
            className="w-full bg-surface border border-border px-4 py-3 pr-11 text-sm"
          />
          <button
            type="button" onClick={() => setShowPassword((v) => !v)} data-testid="sign-in-password-toggle-visibility"
            aria-label={showPassword ? "Hide password" : "Show password"}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="sign-in-submit-button"
          className="w-full bg-foreground text-background px-5 py-3 text-sm uppercase tracking-wide disabled:opacity-50"
        >
          {busy ? "Signing In…" : "Sign In"}
        </button>
        <div className="flex items-center justify-between text-xs text-white/50 pt-2">
          <Link to="/forgot-password" data-testid="sign-in-forgot-password-link" className="underline">Forgot password?</Link>
          <Link to="/sign-up" data-testid="sign-in-sign-up-link" className="underline">Create an account</Link>
        </div>
      </form>
    </div>
  );
}
